/**
 * Интеграция State Machine в CSElement
 */

import { CSElement } from './CSElement';
// import { StateMachine } from '../state-machine/StateMachine';
import { StateMachineManagerImpl } from '../state-machine/StateMachineManager';
import { 
  IStateMachine, 
  StateMachineConfig, 
  StateMachineManager,
  StateMachineEventType,
  StateMachineEventCallback,
  TransitionResult
} from '../types/state-machine-interfaces';

/**
 * Расширение CSElement для поддержки State Machine
 */
export class StateMachineIntegration {
  private static _globalStateMachineManager: StateMachineManager = new StateMachineManagerImpl();

  /**
   * Получить глобальный менеджер State Machine
   */
  static get stateMachineManager(): StateMachineManager {
    return this._globalStateMachineManager;
  }

  /**
   * Создать State Machine для элемента
   */
  static createStateMachine(element: CSElement, config: StateMachineConfig): IStateMachine {
    return this._globalStateMachineManager.create(element, config);
  }

  /**
   * Получить State Machine элемента
   */
  static getStateMachine(element: CSElement): IStateMachine | undefined {
    return this._globalStateMachineManager.get(element.id);
  }

  /**
   * Удалить State Machine элемента
   */
  static removeStateMachine(element: CSElement): boolean {
    return this._globalStateMachineManager.remove(element.id);
  }

  /**
   * Проверить, есть ли у элемента State Machine
   */
  static hasStateMachine(element: CSElement): boolean {
    return this._globalStateMachineManager.has(element.id);
  }

  /**
   * Получить все State Machine
   */
  static getAllStateMachines(): Map<string, IStateMachine> {
    return this._globalStateMachineManager.getAll();
  }

  /**
   * Найти State Machine по состоянию
   */
  static findByState(state: string): IStateMachine[] {
    return this._globalStateMachineManager.findByState(state);
  }

  /**
   * Отправить событие всем State Machine в определенном состоянии
   */
  static async sendToState(state: string, event: string, payload?: any): Promise<Map<string, TransitionResult>> {
    return this._globalStateMachineManager.sendToState(state, event, payload);
  }

  /**
   * Получить статистику State Machine
   */
  static getStats() {
    return this._globalStateMachineManager.getStats();
  }

  /**
   * Очистить все State Machine
   */
  static clear(): void {
    this._globalStateMachineManager.clear();
  }
}

/**
 * Методы для добавления в CSElement
 */
export interface CSElementStateMachineMethods {
  // Создание и управление State Machine
  createStateMachine(config: StateMachineConfig): IStateMachine;
  getStateMachine(): IStateMachine | undefined;
  removeStateMachine(): boolean;
  hasStateMachine(): boolean;

  // Работа с состояниями
  getCurrentState(): string | undefined;
  canTransition(event: string, toState?: string): boolean;
  getPossibleTransitions(): any[];
  getStateHistory(): any[];

  // Отправка событий
  sendStateMachineEvent(event: string, payload?: any): Promise<TransitionResult | undefined>;
  
  // Подписки на события State Machine
  onStateChanged(callback: StateMachineEventCallback): string | undefined;
  onTransitionStarted(callback: StateMachineEventCallback): string | undefined;
  onTransitionCompleted(callback: StateMachineEventCallback): string | undefined;
  onTransitionFailed(callback: StateMachineEventCallback): string | undefined;
  offStateMachineEvent(eventType: StateMachineEventType, callbackId: string): boolean;

  // Управление State Machine
  resetStateMachine(): Promise<void>;
  destroyStateMachine(): void;
}

/**
 * Миксин для добавления методов State Machine в CSElement
 */
export function addStateMachineMethods(CSElementClass: typeof CSElement): void {
  // Создание и управление State Machine
  CSElementClass.prototype.createStateMachine = function(config: StateMachineConfig): IStateMachine {
    if (StateMachineIntegration.hasStateMachine(this)) {
      throw new Error(`State Machine для элемента "${this.id}" уже существует`);
    }
    return StateMachineIntegration.createStateMachine(this, config);
  };

  CSElementClass.prototype.getStateMachine = function(): IStateMachine | undefined {
    return StateMachineIntegration.getStateMachine(this);
  };

  CSElementClass.prototype.removeStateMachine = function(): boolean {
    return StateMachineIntegration.removeStateMachine(this);
  };

  CSElementClass.prototype.hasStateMachine = function(): boolean {
    return StateMachineIntegration.hasStateMachine(this);
  };

  // Работа с состояниями
  CSElementClass.prototype.getCurrentState = function(): string | undefined {
    const sm = this.getStateMachine();
    return sm?.currentState;
  };

  CSElementClass.prototype.canTransition = function(event: string, toState?: string): boolean {
    const sm = this.getStateMachine();
    return sm?.canTransition(event, toState) || false;
  };

  CSElementClass.prototype.getPossibleTransitions = function(): any[] {
    const sm = this.getStateMachine();
    return sm?.getPossibleTransitions() || [];
  };

  CSElementClass.prototype.getStateHistory = function(): any[] {
    const sm = this.getStateMachine();
    return sm?.getHistory() || [];
  };

  // Отправка событий
  CSElementClass.prototype.sendStateMachineEvent = async function(event: string, payload?: any): Promise<TransitionResult | undefined> {
    const sm = this.getStateMachine();
    if (!sm) {
      console.warn(`Элемент "${this.id}" не имеет State Machine`);
      return undefined;
    }
    return await sm.send(event, payload);
  };

  // Подписки на события State Machine
  CSElementClass.prototype.onStateChanged = function(callback: StateMachineEventCallback): string | undefined {
    const sm = this.getStateMachine();
    return sm?.subscribe(StateMachineEventType.STATE_CHANGED, callback);
  };

  CSElementClass.prototype.onTransitionStarted = function(callback: StateMachineEventCallback): string | undefined {
    const sm = this.getStateMachine();
    return sm?.subscribe(StateMachineEventType.TRANSITION_STARTED, callback);
  };

  CSElementClass.prototype.onTransitionCompleted = function(callback: StateMachineEventCallback): string | undefined {
    const sm = this.getStateMachine();
    return sm?.subscribe(StateMachineEventType.TRANSITION_COMPLETED, callback);
  };

  CSElementClass.prototype.onTransitionFailed = function(callback: StateMachineEventCallback): string | undefined {
    const sm = this.getStateMachine();
    return sm?.subscribe(StateMachineEventType.TRANSITION_FAILED, callback);
  };

  CSElementClass.prototype.offStateMachineEvent = function(eventType: StateMachineEventType, callbackId: string): boolean {
    const sm = this.getStateMachine();
    return sm?.unsubscribe(eventType, callbackId) || false;
  };

  // Управление State Machine
  CSElementClass.prototype.resetStateMachine = async function(): Promise<void> {
    const sm = this.getStateMachine();
    if (sm) {
      await sm.reset();
    }
  };

  CSElementClass.prototype.destroyStateMachine = function(): void {
    const sm = this.getStateMachine();
    if (sm) {
      sm.destroy();
      this.removeStateMachine();
    }
  };

  // Статические методы для класса CSElement
  CSElementClass.getStateMachineManager = (): StateMachineManager => {
    return StateMachineIntegration.stateMachineManager;
  };

  CSElementClass.findElementsByState = (state: string): CSElement[] => {
    const stateMachines = StateMachineIntegration.findByState(state);
    return stateMachines
      .map(sm => sm.context.element)
      .filter(el => el instanceof CSElement) as CSElement[];
  };

  CSElementClass.sendEventToElementsInState = async (state: string, event: string, payload?: any): Promise<Map<string, TransitionResult>> => {
    return StateMachineIntegration.sendToState(state, event, payload);
  };

  CSElementClass.getStateMachineStats = () => {
    return StateMachineIntegration.getStats();
  };

  CSElementClass.clearAllStateMachines = (): void => {
    StateMachineIntegration.clear();
  };
}

// Автоматически добавляем методы в CSElement при импорте
addStateMachineMethods(CSElement);

// Перехватываем метод destroy для автоматической очистки State Machine
const originalDestroy = CSElement.prototype.destroy;
CSElement.prototype.destroy = function() {
  // Удаляем State Machine перед уничтожением элемента
  if (this.hasStateMachine()) {
    this.destroyStateMachine();
  }
  
  // Вызываем оригинальный метод destroy
  originalDestroy.call(this);
};

// Декларируем дополнительные методы для TypeScript
declare module './CSElement' {
  interface CSElement extends CSElementStateMachineMethods {}
  
  namespace CSElement {
    function getStateMachineManager(): StateMachineManager;
    function findElementsByState(state: string): CSElement[];
    function sendEventToElementsInState(state: string, event: string, payload?: any): Promise<Map<string, TransitionResult>>;
    function getStateMachineStats(): any;
    function clearAllStateMachines(): void;
  }
} 